import React, { useEffect, useMemo, useState } from 'react';
import { attendanceUiText } from '@/lib/nfcUiVisibility';
import {
  AlertCircle,
  BarChart3,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Clock3,
  FileClock,
  Loader2,
  RefreshCw,
  Search,
  ShieldAlert,
  SlidersHorizontal,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/supabaseClient';
import { formatAttendanceHours as formatHours } from '@/lib/attendanceHours';
import HoursPdfExport from '@/components/HoursPdfExport';
import { useSelfEmployee } from '@/lib/selfIdentity';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const STATUS_META = {
  APERTA: { label: 'Aperta', className: 'border-blue-200 bg-blue-50 text-blue-800' },
  CHIUSA: { label: 'Chiusa', className: 'border-emerald-200 bg-emerald-50 text-emerald-800' },
  DA_VALIDARE: { label: 'Da validare', className: 'border-amber-200 bg-amber-50 text-amber-800' },
};

const EVENT_TYPE_LABELS = {
  ENTRY: 'Entrata',
  EXIT: 'Uscita',
  UNKNOWN: 'Sconosciuto',
};

const EVENT_SOURCE_LABELS = {
  APP: 'App',
  NFC_SUN_VERIFIED: 'NFC verificato',
};

const EMPTY_PAGE = { items: [], meta: { page: 1, page_size: 25, total: 0, pages: 0 } };

function collection(value) {
  if (Array.isArray(value)) return value;
  return value?.items || value?.data || value?.records || [];
}

function startOfCurrentMonth() {
  const date = new Date();
  const pad = (number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-01`;
}

function today() {
  const date = new Date();
  const pad = (number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function initialFilters() {
  return {
    date_from: startOfCurrentMonth(),
    date_to: today(),
    company_id: '',
    location_id: '',
    department_id: '',
    employee_id: '',
    function: '',
    status: '',
  };
}

function filterQuery(filters) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  return params;
}

function sameId(left, right) {
  return left != null && right != null && String(left) === String(right);
}

function departmentMatchesHierarchy(department, companyId, locationId, locations) {
  if (locationId && !sameId(department.location_id, locationId)) return false;
  if (!companyId) return true;
  const location = locations.find((item) => sameId(item.id, department.location_id));
  return Boolean(location && sameId(location.company_id, companyId));
}

function personMatchesHierarchy(person, filters, companies, locations, departments) {
  const company = companies.find((item) => sameId(item.id, filters.company_id));
  const location = locations.find((item) => sameId(item.id, filters.location_id));
  const department = departments.find((item) => sameId(item.id, filters.department_id));

  if (company) {
    if (person.company_id != null && !sameId(person.company_id, company.id)) return false;
    if (person.company_id == null && person.company_name && person.company_name !== company.name) return false;
  }
  if (location) {
    if (person.location_id != null && !sameId(person.location_id, location.id)) return false;
    if (person.location_id == null && person.primary_location && person.primary_location !== location.name) return false;
  }
  if (department) {
    if (person.department_id != null && !sameId(person.department_id, department.id)) return false;
    if (person.department_id == null && person.department && person.department !== department.name) return false;
  }
  if (filters.function && person.job_title !== filters.function) return false;
  return true;
}

function sanitizeFilterSelection(next, companies, locations, departments, people) {
  const selectedLocation = locations.find((item) => sameId(item.id, next.location_id));
  if (
    next.location_id
    && (!selectedLocation || (next.company_id && !sameId(selectedLocation.company_id, next.company_id)))
  ) {
    next.location_id = '';
  }

  const selectedDepartment = departments.find((item) => sameId(item.id, next.department_id));
  if (
    next.department_id
    && (!selectedDepartment || !departmentMatchesHierarchy(
      selectedDepartment,
      next.company_id,
      next.location_id,
      locations,
    ))
  ) {
    next.department_id = '';
  }

  const hierarchyFilters = { ...next, function: '' };
  const availableFunctions = new Set(
    people
      .filter((person) => personMatchesHierarchy(person, hierarchyFilters, companies, locations, departments))
      .map((person) => person.job_title)
      .filter(Boolean),
  );
  if (next.function && !availableFunctions.has(next.function)) {
    next.function = '';
  }

  if (
    next.employee_id
    && !people.some((person) => (
      sameId(person.id, next.employee_id)
      && personMatchesHierarchy(person, next, companies, locations, departments)
    ))
  ) {
    next.employee_id = '';
  }
  return next;
}

function formatInteger(value) {
  return typeof value === 'number' && Number.isFinite(value)
    ? new Intl.NumberFormat('it-IT').format(value)
    : '—';
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function toDateTimeLocal(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function toIso(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function statusBadge(status) {
  const meta = STATUS_META[status] || { label: status || '—', className: 'border-stone-200 bg-stone-50 text-stone-600' };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-bold ${meta.className}`}>
      {meta.label}
    </span>
  );
}

function FilterSelect({ label, value, onChange, options, disabled = false, testId }) {
  return (
    <div className="min-w-0 space-y-0.5">
      <Label htmlFor={testId} className="text-[10px] leading-tight text-stone-600">{label}</Label>
      <select
        id={testId}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        data-testid={testId}
        className="h-10 w-full min-w-0 rounded-lg border border-app-border-strong bg-app-surface px-2 text-xs text-app-ink shadow-sm outline-none transition focus:border-brand-green focus:ring-2 focus:ring-emerald-100 disabled:cursor-not-allowed disabled:bg-stone-50 disabled:text-stone-400 md:h-9"
      >
        <option value="">Tutti</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, tone = 'green', testId, className = '' }) {
  const toneClasses = {
    green: 'bg-emerald-50 text-brand-green',
    blue: 'bg-blue-50 text-blue-700',
    amber: 'bg-amber-50 text-amber-700',
    navy: 'bg-slate-100 text-brand-navy',
  };
  return (
    <article className={`rounded-xl border border-app-border bg-app-surface p-2.5 shadow-sm md:p-3 ${className}`} data-testid={testId}>
      <div className="flex items-start justify-between gap-1.5">
        <div>
          <div className="text-[9px] font-bold uppercase leading-tight tracking-[0.08em] text-stone-500">{label}</div>
          <div className="mt-0.5 text-lg font-semibold leading-tight tracking-tight text-brand-navy md:text-xl">{value}</div>
        </div>
        <div className={`rounded-lg p-1.5 ${toneClasses[tone] || toneClasses.green}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </article>
  );
}

function PresenceCard({ record, onValidate, onApproveQrEntry, onCorrect }) {
  const canApproveQrEntry = record.status === 'DA_VALIDARE'
    && record.entry_source === 'QR_DYNAMIC'
    && record.entry_reason_code === 'EMPLOYEE_LOCATION_NOT_AUTHORIZED'
    && !record.exit_event_id
    && !record.exited_at_utc;
  return (
    <article className="rounded-xl border border-app-border bg-app-surface p-2.5 shadow-sm md:p-3" data-testid={`attendance-card-${record.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.13em] text-brand-green">{record.company_name}</div>
          <h3 className="mt-0.5 truncate text-sm font-semibold text-brand-navy">{record.employee_name || 'Dipendente non disponibile'}</h3>
          <p className="mt-0.5 truncate text-[11px] text-stone-500">
            {record.location_name} · {record.department_name || 'Senza reparto'}
          </p>
        </div>
        <span className="shrink-0">{statusBadge(record.status)}</span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1.5 rounded-lg bg-stone-50 p-2 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-wide text-stone-400">Entrata</div>
          <div className="mt-0.5 font-semibold text-stone-800">{formatDateTime(record.entered_at_utc)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-stone-400">Uscita</div>
          <div className="mt-0.5 font-semibold text-stone-800">{formatDateTime(record.exited_at_utc)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-stone-400">Ore ufficiali</div>
          <div className="mt-0.5 font-semibold text-brand-navy">{record.status === 'CHIUSA' ? formatHours(record.hours_seconds) : 'Non conteggiate'}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wide text-stone-400">Data locale</div>
          <div className="mt-0.5 font-semibold text-stone-800">{formatDate(record.local_date)}</div>
        </div>
      </div>
      {record.status === 'DA_VALIDARE' && (
        <div className="mt-2 flex flex-wrap gap-2">
          {canApproveQrEntry ? (
            <Button size="sm" onClick={() => onApproveQrEntry(record)} data-testid={`attendance-approve-qr-entry-${record.id}`}>
              <Check className="h-3.5 w-3.5" /> Approva entrata QR
            </Button>
          ) : record.exited_at_utc ? (
            <Button size="sm" onClick={() => onValidate(record)} data-testid={`attendance-validate-${record.id}`}>
              <Check className="h-3.5 w-3.5" /> Valida
            </Button>
          ) : null}
          <Button size="sm" variant="outline" onClick={() => onCorrect(record)} data-testid={`attendance-correct-${record.id}`}>
            Correggi
          </Button>
        </div>
      )}
    </article>
  );
}

function PresenceGroupList({ records, onValidate, onApproveQrEntry, onCorrect }) {
  if (!records.length) {
    return (
      <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 px-4 py-4 text-center" data-testid="attendance-empty">
        <Clock3 className="mx-auto h-5 w-5 text-stone-300" />
        <h3 className="mt-1.5 text-sm font-semibold text-brand-navy">Nessuna presenza trovata</h3>
        <p className="mt-0.5 text-xs text-stone-500">Prova a cambiare periodo o filtri.</p>
      </div>
    );
  }

  let lastCompany = null;
  let lastLocation = null;
  let lastDepartment = null;
  return (
    <div className="space-y-3" data-testid="attendance-hierarchy">
      {records.map((record) => {
        const companyChanged = record.company_name !== lastCompany;
        const locationChanged = companyChanged || record.location_name !== lastLocation;
        const departmentChanged = locationChanged || (record.department_name || '') !== lastDepartment;
        if (companyChanged) lastCompany = record.company_name;
        if (locationChanged) lastLocation = record.location_name;
        if (departmentChanged) lastDepartment = record.department_name || '';
        return (
          <section key={record.id} className="space-y-1.5">
            {(companyChanged || locationChanged || departmentChanged) && (
              <div className="space-y-0.5 px-1">
                {companyChanged && <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-brand-green">{record.company_name}</div>}
                {locationChanged && <div className="text-sm font-semibold text-brand-navy">{record.location_name}</div>}
                {departmentChanged && <div className="text-[11px] text-stone-500">{record.department_name || 'Senza reparto'}</div>}
              </div>
            )}
            <PresenceCard
              record={record}
              onValidate={onValidate}
              onApproveQrEntry={onApproveQrEntry}
              onCorrect={onCorrect}
            />
          </section>
        );
      })}
    </div>
  );
}

function Totals({ groups }) {
  const total = groups.reduce((sum, group) => sum + (Number(group.official_hours_seconds) || 0), 0);
  return (
    <section className="rounded-xl border border-app-border bg-app-surface shadow-sm" data-testid="attendance-totals">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-app-border px-3 py-3 md:px-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-brand-green">Totali ufficiali</div>
          <h2 className="mt-0.5 text-base font-semibold text-brand-navy">Ore per reparto e dipendente</h2>
        </div>
        <div className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-brand-green">
          Totale: {formatHours(total)}
        </div>
      </div>
      {groups.length === 0 ? (
        <div className="px-3 py-4 text-center text-xs text-stone-500">Nessun totale disponibile per i filtri selezionati.</div>
      ) : (
        <div className="divide-y divide-stone-100">
          {groups.map((group) => (
            <div key={`${group.company_id}-${group.location_id}-${group.department_id || 'none'}-${group.employee_id}`} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm md:px-4">
              <div className="min-w-0">
                <div className="truncate font-semibold text-brand-navy">{group.employee_name}</div>
                <div className="truncate text-xs text-stone-500">
                  {group.company_name} · {group.location_name} · {group.department_name || 'Senza reparto'}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="font-semibold text-brand-navy">{formatHours(group.official_hours_seconds)}</div>
                <div className="text-[11px] text-stone-400">{formatInteger(group.presence_count)} presenze</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function EventRegistry({ events, loading, onRefresh }) {
  return (
    <section className="space-y-3" data-testid="attendance-events">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.16em] text-brand-green">Accesso secondario</div>
          <h2 className="mt-0.5 text-lg font-semibold text-brand-navy">Registro eventi</h2>
          <p className="mt-0.5 text-xs text-stone-500">Eventi tecnici leggibili, senza dati sensibili.</p>
        </div>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="attendance-events-refresh">
          <RefreshCw className={loading ? 'animate-spin' : ''} /> Aggiorna
        </Button>
      </div>
      {loading ? (
        <div className="flex items-center justify-center rounded-xl border border-app-border bg-app-surface py-8 text-sm text-stone-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Caricamento eventi…</div>
      ) : !events.items.length ? (
        <div className="rounded-xl border border-dashed border-stone-300 bg-stone-50 px-4 py-6 text-center text-xs text-stone-500" data-testid="attendance-events-empty">Nessun evento per i filtri selezionati.</div>
      ) : (
        <div className="space-y-2">
          {events.items.map((event) => (
            <article key={event.id} className="rounded-xl border border-app-border bg-app-surface p-3 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-brand-navy">{event.employee_name}</div>
                  <div className="mt-0.5 text-[11px] text-stone-500">{event.company_name} · {event.location_name} · {event.department_name || 'Senza reparto'}</div>
                </div>
                <span className="rounded-full border border-stone-200 bg-stone-50 px-2.5 py-1 text-[11px] font-bold text-stone-700">
                  {attendanceUiText(EVENT_TYPE_LABELS[event.event_type] || event.event_type || 'Evento')}
                </span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-stone-600 md:grid-cols-4">
                <div><span className="block text-[10px] uppercase tracking-wide text-stone-400">Quando</span>{formatDateTime(event.occurred_at || event.received_at)}</div>
                <div><span className="block text-[10px] uppercase tracking-wide text-stone-400">Fonte</span>{attendanceUiText(EVENT_SOURCE_LABELS[event.source] || event.source || '—')}</div>
                <div><span className="block text-[10px] uppercase tracking-wide text-stone-400">Verifica</span>{event.verification_status || '—'}</div>
                <div><span className="block text-[10px] uppercase tracking-wide text-stone-400">Autorizzazione</span>{event.authorization_status || '—'}</div>
              </div>
              {event.reason_code && <div className="mt-2 rounded-lg bg-stone-50 px-3 py-2 text-xs text-stone-600">Esito tecnico originale: {attendanceUiText(event.reason_code)}</div>}
              {event.authorization_review_reason && (
                <div className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-900" data-testid={`attendance-event-review-${event.id}`}>
                  Revisione amministrativa: {event.authorization_review_reason}
                </div>
              )}
            </article>
          ))}
        </div>
      )}
      {events.meta.pages > 1 && (
          <div className="flex items-center justify-between rounded-xl border border-app-border bg-app-surface px-3 py-2.5 text-sm">
          <span className="text-stone-500">Pagina {events.meta.page} di {events.meta.pages}</span>
          <span className="text-xs text-stone-400">{formatInteger(events.meta.total)} eventi</span>
        </div>
      )}
    </section>
  );
}

export default function Attendance() {
  const { isSimulated: employeeSimulation } = useSelfEmployee();
  const { currentRoleId, devImpersonateRoleId, devImpersonatePersonaId } = useApp();
  const isSimulated = employeeSimulation
    || (currentRoleId === 'ceo' && Boolean(devImpersonateRoleId || devImpersonatePersonaId));
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [people, setPeople] = useState([]);
  const [page, setPage] = useState(1);
  const [records, setRecords] = useState(EMPTY_PAGE);
  const [summary, setSummary] = useState({ kpis: {}, groups: [] });
  const [events, setEvents] = useState(EMPTY_PAGE);
  const [activeTab, setActiveTab] = useState('presenze');
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [additionalFiltersOpen, setAdditionalFiltersOpen] = useState(false);
  const [error, setError] = useState('');
  const [actionRecord, setActionRecord] = useState(null);
  const [qrApprovalRecord, setQrApprovalRecord] = useState(null);
  const [qrApprovalReason, setQrApprovalReason] = useState('');
  const [correction, setCorrection] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadCatalog = async () => {
    setCatalogLoading(true);
    try {
      const [companyRows, locationRows, peopleRows] = await Promise.all([
        apiFetch('/api/companies'),
        apiFetch('/api/locations'),
        apiFetch('/api/personale'),
      ]);
      const normalizedLocations = collection(locationRows);
      const departmentLists = await Promise.all(
        normalizedLocations.map(async (location) => {
          const rows = await apiFetch(`/api/departments?location_id=${encodeURIComponent(location.id)}`);
          return collection(rows).map((department) => ({ ...department, location_id: location.id }));
        }),
      );
      setCompanies(collection(companyRows));
      setLocations(normalizedLocations);
      setDepartments(departmentLists.flat());
      setPeople(collection(peopleRows));
    } catch (loadError) {
      setError(loadError.message || 'Impossibile caricare i filtri autorizzati.');
    } finally {
      setCatalogLoading(false);
    }
  };

  useEffect(() => {
    loadCatalog();
  }, []);

  const loadData = async (nextPage = page) => {
    setLoading(true);
    setError('');
    const query = filterQuery(appliedFilters);
    query.set('page', String(nextPage));
    query.set('page_size', '25');
    try {
      const [recordPage, summaryData] = await Promise.all([
        apiFetch(`/api/attendance?${query.toString()}`),
        apiFetch(`/api/attendance/summary?${filterQuery(appliedFilters).toString()}`),
      ]);
      setRecords(recordPage || EMPTY_PAGE);
      setSummary(summaryData || { kpis: {}, groups: [] });
    } catch (loadError) {
      setError(loadError.message || 'Impossibile caricare le presenze.');
      setRecords(EMPTY_PAGE);
      setSummary({ kpis: {}, groups: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(page);
    // The filters are serialized to keep the effect tied to the applied query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, JSON.stringify(appliedFilters)]);

  const loadEvents = async () => {
    setEventsLoading(true);
    try {
      const query = filterQuery(appliedFilters);
      query.set('page', '1');
      query.set('page_size', '50');
      const eventPage = await apiFetch(`/api/attendance/events?${query.toString()}`);
      setEvents(eventPage || EMPTY_PAGE);
    } catch (loadError) {
      toast.error(loadError.message || 'Impossibile caricare il registro eventi.');
      setEvents(EMPTY_PAGE);
    } finally {
      setEventsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'eventi') loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, JSON.stringify(appliedFilters)]);

  const availableLocations = useMemo(
    () => locations.filter((location) => !filters.company_id || String(location.company_id) === String(filters.company_id)),
    [locations, filters.company_id],
  );
  const availableDepartments = useMemo(
    () => departments.filter((department) => departmentMatchesHierarchy(
      department,
      filters.company_id,
      filters.location_id,
      locations,
    )),
    [departments, locations, filters.company_id, filters.location_id],
  );
  const hierarchyPeople = useMemo(
    () => people
      .filter((person) => personMatchesHierarchy(
        person,
        {
          company_id: filters.company_id,
          location_id: filters.location_id,
          department_id: filters.department_id,
          function: '',
        },
        companies,
        locations,
        departments,
      ))
      .sort((a, b) => String(a.display_name || '').localeCompare(String(b.display_name || ''), 'it')),
    [people, companies, locations, departments, filters.company_id, filters.location_id, filters.department_id],
  );
  const availablePeople = useMemo(
    () => hierarchyPeople.filter((person) => !filters.function || person.job_title === filters.function),
    [hierarchyPeople, filters.function],
  );
  const functionOptions = useMemo(() => (
    Array.from(new Set(hierarchyPeople.map((person) => person.job_title).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, 'it'))
      .map((value) => ({ value, label: value }))
  ), [hierarchyPeople]);
  const activeFilterCount = [
    filters.company_id,
    filters.location_id,
    filters.department_id,
    filters.employee_id,
    filters.function,
    filters.status,
  ].filter(Boolean).length;

  const updateFilter = (key, value) => {
    setFilters((current) => {
      const next = { ...current, [key]: value };
      return sanitizeFilterSelection(next, companies, locations, departments, people);
    });
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setAppliedFilters({ ...filters });
  };

  const clearFilters = () => {
    const next = initialFilters();
    setFilters(next);
    setPage(1);
    setAppliedFilters(next);
  };

  const refresh = () => loadData(page);

  const openCorrection = (record) => {
    setCorrection({
      record,
      form: {
        reason: '',
        status: record.status,
        entered_at_utc: toDateTimeLocal(record.entered_at_utc),
        exited_at_utc: toDateTimeLocal(record.exited_at_utc),
        local_date: record.local_date || '',
        department_id: record.department_id || '',
      },
    });
  };

  const validateRecord = async () => {
    if (!actionRecord || saving) return;
    setSaving(true);
    try {
      await apiFetch(`/api/attendance/${actionRecord.id}/validate`, { method: 'POST' });
      toast.success('Presenza validata');
      setActionRecord(null);
      await loadData(page);
    } catch (saveError) {
      toast.error(saveError.message || 'Validazione non riuscita.');
    } finally {
      setSaving(false);
    }
  };

  const openQrEntryApproval = (record) => {
    setQrApprovalReason('');
    setQrApprovalRecord(record);
  };

  const approveQrEntry = async () => {
    const reason = qrApprovalReason.trim();
    if (!qrApprovalRecord || saving || reason.length < 10) return;
    setSaving(true);
    try {
      await apiFetch(`/api/attendance/${qrApprovalRecord.id}/approve-qr-entry`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      toast.success('Entrata QR approvata e resa ufficiale');
      setQrApprovalRecord(null);
      setQrApprovalReason('');
      await loadData(page);
    } catch (saveError) {
      toast.error(saveError.message || 'Approvazione dell’entrata QR non riuscita.');
    } finally {
      setSaving(false);
    }
  };

  const correctRecord = async () => {
    if (!correction || saving || !correction.form.reason.trim()) return;
    setSaving(true);
    const { form } = correction;
    try {
      await apiFetch(`/api/attendance/${correction.record.id}/corrections`, {
        method: 'POST',
        body: JSON.stringify({
          reason: form.reason.trim(),
          status: form.status,
          entered_at_utc: toIso(form.entered_at_utc),
          exited_at_utc: toIso(form.exited_at_utc),
          local_date: form.local_date || null,
          department_id: form.department_id || null,
        }),
      });
      toast.success('Correzione registrata');
      setCorrection(null);
      await loadData(page);
    } catch (saveError) {
      toast.error(saveError.message || 'Correzione non riuscita.');
    } finally {
      setSaving(false);
    }
  };

  const kpis = summary.kpis || {};
  const totalPages = records.meta?.pages || 0;

  return (
    <div className="mx-auto max-w-7xl space-y-2.5 md:space-y-4" data-testid="attendance-page">
      <header className="flex flex-wrap items-start justify-between gap-3" data-testid="attendance-header">
        <div className="min-w-0">
          <div className="text-[9px] font-bold uppercase tracking-[0.16em] text-brand-green">Controllo operativo</div>
          <div className="flex items-center justify-between gap-2">
            <h1 className="mt-0.5 font-display text-2xl font-semibold leading-none tracking-tight text-brand-navy md:text-4xl">Presenze</h1>
            <Button
              variant="outline"
              size="sm"
              className="h-8 shrink-0 gap-1.5 px-2.5 text-xs md:h-9 md:px-3"
              onClick={refresh}
              disabled={loading}
              data-testid="attendance-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Aggiorna
            </Button>
          </div>
          <p className="mt-1 max-w-2xl text-[11px] leading-tight text-stone-500">Controllo delle presenze e delle ore lavorate.</p>
        </div>
      </header>

      {error && (
        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-2.5 text-xs text-red-800" role="alert" data-testid="attendance-error">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <div><div className="font-semibold">Impossibile completare il caricamento</div><div className="mt-0.5">{error}</div></div>
        </div>
      )}

      <form onSubmit={applyFilters} className="rounded-xl border border-app-border bg-app-surface p-2 shadow-sm md:p-3" data-testid="attendance-filters">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="rounded-md bg-emerald-50 p-1 text-brand-green"><SlidersHorizontal className="h-3.5 w-3.5" /></div>
            <div><h2 className="text-sm font-semibold leading-tight text-brand-navy">Filtri</h2><p className="text-[10px] leading-tight text-stone-500">Dati nel perimetro autorizzato</p></div>
          </div>
          <button type="button" onClick={clearFilters} className="text-xs font-semibold text-stone-500 hover:text-brand-green" data-testid="attendance-clear-filters">Azzera</button>
        </div>
        <div className="grid grid-cols-2 gap-x-2 gap-y-1 md:grid-cols-4 md:gap-2">
          <div className="attendance-date-grid col-span-2 grid w-full min-w-0 max-w-full" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', columnGap: '16px', width: '100%', minWidth: 0 }} data-testid="attendance-date-filters">
            <div className="attendance-date-field w-full min-w-0 space-y-0.5" style={{ width: '100%', minWidth: 0 }} data-testid="attendance-date-from-wrapper">
              <Label htmlFor="attendance-date-from" className="text-[10px] leading-tight text-stone-600">Data iniziale</Label>
              <div className="attendance-date-control box-border w-full min-w-0 overflow-hidden" style={{ width: '100%', minWidth: 0, overflow: 'hidden', boxSizing: 'border-box' }} data-testid="attendance-date-from-control">
                <Input className="box-border h-10 w-full min-w-0 max-w-full text-xs md:h-9" style={{ display: 'block', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }} id="attendance-date-from" type="date" value={filters.date_from} onChange={(event) => updateFilter('date_from', event.target.value)} data-testid="attendance-date-from" />
              </div>
            </div>
            <div className="attendance-date-field w-full min-w-0 space-y-0.5" style={{ width: '100%', minWidth: 0 }} data-testid="attendance-date-to-wrapper">
              <Label htmlFor="attendance-date-to" className="text-[10px] leading-tight text-stone-600">Data finale</Label>
              <div className="attendance-date-control box-border w-full min-w-0 overflow-hidden" style={{ width: '100%', minWidth: 0, overflow: 'hidden', boxSizing: 'border-box' }} data-testid="attendance-date-to-control">
                <Input className="box-border h-10 w-full min-w-0 max-w-full text-xs md:h-9" style={{ display: 'block', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }} id="attendance-date-to" type="date" value={filters.date_to} onChange={(event) => updateFilter('date_to', event.target.value)} data-testid="attendance-date-to" />
              </div>
            </div>
          </div>
          <FilterSelect label="Azienda" value={filters.company_id} onChange={(value) => updateFilter('company_id', value)} options={companies.map((item) => ({ value: item.id, label: item.name }))} disabled={catalogLoading} testId="attendance-company" />
          <FilterSelect label="Locale" value={filters.location_id} onChange={(value) => updateFilter('location_id', value)} options={availableLocations.map((item) => ({ value: item.id, label: item.name }))} disabled={catalogLoading || (!filters.company_id && !availableLocations.length)} testId="attendance-location" />
          <div className="col-span-2 min-w-0 md:col-span-1">
            <FilterSelect label="Reparto" value={filters.department_id} onChange={(value) => updateFilter('department_id', value)} options={availableDepartments.map((item) => ({ value: item.id, label: item.name }))} disabled={catalogLoading || (!filters.location_id && !availableDepartments.length)} testId="attendance-department" />
          </div>
        </div>
        <div className="mt-1.5 border-t border-stone-100 pt-1.5">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              className="inline-flex min-h-8 items-center gap-1 rounded-md px-1 text-xs font-semibold text-brand-navy transition hover:bg-stone-50 hover:text-brand-green focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-100"
              onClick={() => setAdditionalFiltersOpen((current) => !current)}
              aria-expanded={additionalFiltersOpen}
              aria-controls="attendance-additional-filters"
              data-testid="attendance-additional-toggle"
            >
              {additionalFiltersOpen ? <ChevronUp className="h-3.5 w-3.5 text-brand-green" /> : <ChevronDown className="h-3.5 w-3.5 text-brand-green" />}
              Altri filtri
            </button>
            {activeFilterCount > 0 && (
              <span className="text-[10px] font-medium text-stone-500" data-testid="attendance-active-filters">
                {activeFilterCount} {activeFilterCount === 1 ? 'filtro attivo' : 'filtri attivi'}
              </span>
            )}
          </div>
          {additionalFiltersOpen && (
            <div id="attendance-additional-filters" className="mt-1 grid grid-cols-2 gap-x-2 gap-y-1 md:grid-cols-3 md:gap-2" data-testid="attendance-additional-filters">
              <div className="col-span-2 md:col-span-1">
                <FilterSelect label="Dipendente" value={filters.employee_id} onChange={(value) => updateFilter('employee_id', value)} options={availablePeople.map((item) => ({ value: item.id, label: item.display_name }))} disabled={catalogLoading} testId="attendance-employee" />
              </div>
              <FilterSelect label="Funzione" value={filters.function} onChange={(value) => updateFilter('function', value)} options={functionOptions} disabled={catalogLoading} testId="attendance-function" />
              <FilterSelect label="Stato" value={filters.status} onChange={(value) => updateFilter('status', value)} options={Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label }))} testId="attendance-status" />
            </div>
          )}
        </div>
        <div className="mt-1.5 flex justify-end">
          <Button type="submit" size="sm" className="h-9 gap-1.5 px-3 text-xs" disabled={loading} data-testid="attendance-apply-filters"><Search className="h-3.5 w-3.5" /> Applica filtri</Button>
        </div>
      </form>

      <section className="grid grid-cols-2 gap-2 md:grid-cols-5 md:gap-3" data-testid="attendance-kpis">
        <KpiCard label="Persone al lavoro" value={formatInteger(kpis.people_at_work)} icon={ClipboardCheck} tone="green" testId="attendance-kpi-people" />
        <KpiCard label="Ore ufficiali" value={formatHours(kpis.official_hours_seconds)} icon={Clock3} tone="blue" testId="attendance-kpi-hours" />
        <KpiCard label="Locali attivi" value={formatInteger(kpis.active_locations)} icon={BarChart3} tone="navy" testId="attendance-kpi-locations" />
        <KpiCard label="Presenze aperte" value={formatInteger(kpis.open_presences)} icon={FileClock} tone="blue" testId="attendance-kpi-open" />
        <KpiCard label="Da validare" value={formatInteger(kpis.da_validare)} icon={ShieldAlert} tone="amber" testId="attendance-kpi-validate" className="col-span-2 md:col-span-1" />
      </section>

      <HoursPdfExport
        mode="admin"
        people={availablePeople}
        companies={companies}
        locations={locations}
        departments={departments}
        filters={appliedFilters}
        isSimulated={isSimulated}
        testId="attendance-hours-pdf"
      />

      <div className="flex gap-1 overflow-x-auto border-b border-app-border" role="tablist">
        <button type="button" role="tab" aria-selected={activeTab === 'presenze'} onClick={() => setActiveTab('presenze')} className={`whitespace-nowrap border-b-2 px-2.5 py-2 text-xs font-semibold sm:text-sm ${activeTab === 'presenze' ? 'border-brand-green text-brand-green' : 'border-transparent text-stone-500'}`} data-testid="attendance-tab-presenze">Presenze</button>
        <button type="button" role="tab" aria-selected={activeTab === 'eventi'} onClick={() => setActiveTab('eventi')} className={`whitespace-nowrap border-b-2 px-2.5 py-2 text-xs font-semibold sm:text-sm ${activeTab === 'eventi' ? 'border-brand-green text-brand-green' : 'border-transparent text-stone-500'}`} data-testid="attendance-tab-events">Registro eventi</button>
      </div>

      {activeTab === 'presenze' ? (
        <>
          <section className="space-y-2" data-testid="attendance-list-section">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div><div className="text-[9px] font-bold uppercase tracking-[0.14em] text-brand-green">Elenco gerarchico</div><h2 className="mt-0.5 text-base font-semibold text-brand-navy">Presenze registrate</h2></div>
              {records.meta && <div className="text-[11px] text-stone-500">{formatInteger(records.meta.total)} risultati</div>}
            </div>
            {loading ? <div className="flex items-center justify-center rounded-xl border border-app-border bg-app-surface py-8 text-sm text-stone-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Caricamento presenze…</div> : <PresenceGroupList records={records.items || []} onValidate={setActionRecord} onApproveQrEntry={openQrEntryApproval} onCorrect={openCorrection} />}
            {totalPages > 1 && (
              <div className="flex items-center justify-between rounded-xl border border-app-border bg-app-surface px-3 py-2.5">
                <Button variant="outline" size="sm" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1 || loading}><ChevronLeft /> Precedente</Button>
                <span className="text-xs text-stone-500">Pagina {page} di {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={page >= totalPages || loading}>Successiva <ChevronRight /></Button>
              </div>
            )}
          </section>
          <Totals groups={summary.groups || []} />
        </>
      ) : (
        <EventRegistry events={events} loading={eventsLoading} onRefresh={loadEvents} />
      )}

      <Dialog open={Boolean(actionRecord)} onOpenChange={(open) => !open && !saving && setActionRecord(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Validare la presenza?</DialogTitle>
            <DialogDescription>La presenza di {actionRecord?.employee_name || 'questo dipendente'} verrà portata a CHIUSA. Le ore entreranno nei totali ufficiali; gli eventi tecnici originali resteranno invariati.</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            Verifica che l&apos;uscita sia coerente prima di confermare.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionRecord(null)} disabled={saving}>Annulla</Button>
            <Button onClick={validateRecord} disabled={saving}>{saving && <Loader2 className="animate-spin" />} Conferma validazione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(qrApprovalRecord)}
        onOpenChange={(open) => {
          if (!open && !saving) {
            setQrApprovalRecord(null);
            setQrApprovalReason('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approvare l’entrata QR?</DialogTitle>
            <DialogDescription>
              L’evento QR originale resterà invariato. Verrà registrata una decisione amministrativa e la presenza diventerà aperta; nessuna uscita verrà creata.
            </DialogDescription>
          </DialogHeader>
          {qrApprovalRecord && (
            <>
              <div className="rounded-xl border border-stone-200 bg-stone-50 p-3 text-sm">
                <div className="font-semibold text-brand-navy">{qrApprovalRecord.employee_name}</div>
                <div className="mt-1 text-xs text-stone-500">
                  {qrApprovalRecord.company_name} · {qrApprovalRecord.location_name} · {formatDateTime(qrApprovalRecord.entered_at_utc)}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="qr-entry-approval-reason">Motivazione amministrativa *</Label>
                <Textarea
                  id="qr-entry-approval-reason"
                  value={qrApprovalReason}
                  onChange={(event) => setQrApprovalReason(event.target.value)}
                  placeholder="Indica la verifica effettuata e il motivo dell’approvazione…"
                  rows={4}
                  data-testid="attendance-qr-approval-reason"
                />
                <p className="text-xs text-stone-500">Inserisci almeno 10 caratteri. Il backend ricontrolla tenant, punto, assegnazione, token consumato e assenza di altre presenze aperte.</p>
              </div>
            </>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setQrApprovalRecord(null);
                setQrApprovalReason('');
              }}
              disabled={saving}
            >
              Annulla
            </Button>
            <Button
              onClick={approveQrEntry}
              disabled={saving || qrApprovalReason.trim().length < 10}
              data-testid="attendance-qr-approval-submit"
            >
              {saving && <Loader2 className="animate-spin" />} Approva entrata
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(correction)} onOpenChange={(open) => !open && !saving && setCorrection(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Correggere la presenza</DialogTitle>
            <DialogDescription>La correzione è append-only e viene registrata con audit. Gli eventi tecnici originali non vengono modificati.</DialogDescription>
          </DialogHeader>
          {correction && (
            <div className="space-y-4">
              <div className="rounded-xl bg-stone-50 p-3 text-sm">
                <div className="font-semibold text-brand-navy">{correction.record.employee_name}</div>
                <div className="mt-1 text-xs text-stone-500">{correction.record.company_name} · {correction.record.location_name}</div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5"><Label htmlFor="correction-status">Stato</Label><select id="correction-status" value={correction.form.status} onChange={(event) => setCorrection((current) => ({ ...current, form: { ...current.form, status: event.target.value } }))} className="h-10 w-full rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm" data-testid="attendance-correction-status">{Object.entries(STATUS_META).filter(([value]) => value !== 'APERTA' || correction.record.status === 'APERTA').map(([value, meta]) => <option key={value} value={value}>{meta.label}</option>)}</select></div>
                <div className="space-y-1.5"><Label htmlFor="correction-date">Data locale</Label><Input id="correction-date" type="date" value={correction.form.local_date} onChange={(event) => setCorrection((current) => ({ ...current, form: { ...current.form, local_date: event.target.value } }))} data-testid="attendance-correction-date" /></div>
                <div className="space-y-1.5"><Label htmlFor="correction-entry">Entrata</Label><Input id="correction-entry" type="datetime-local" value={correction.form.entered_at_utc} onChange={(event) => setCorrection((current) => ({ ...current, form: { ...current.form, entered_at_utc: event.target.value } }))} data-testid="attendance-correction-entry" /></div>
                <div className="space-y-1.5"><Label htmlFor="correction-exit">Uscita</Label><Input id="correction-exit" type="datetime-local" value={correction.form.exited_at_utc} onChange={(event) => setCorrection((current) => ({ ...current, form: { ...current.form, exited_at_utc: event.target.value } }))} data-testid="attendance-correction-exit" /></div>
              </div>
              <div className="space-y-1.5"><Label htmlFor="correction-reason">Motivazione *</Label><Textarea id="correction-reason" value={correction.form.reason} onChange={(event) => setCorrection((current) => ({ ...current, form: { ...current.form, reason: event.target.value } }))} placeholder="Descrivi perché la presenza deve essere corretta…" rows={4} data-testid="attendance-correction-reason" /></div>
              <p className="text-xs text-stone-500">La timezone e il contesto Azienda/Locale vengono verificati dal backend e non possono essere spostati liberamente dal frontend.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrection(null)} disabled={saving}>Annulla</Button>
            <Button onClick={correctRecord} disabled={saving || !correction?.form.reason.trim()}>{saving && <Loader2 className="animate-spin" />} Registra correzione</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}